import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../../utils/cn';

const Tooltip = ({ 
  children, 
  content, 
  position = 'top',
  delay = 500,
  className = '',
  disabled = false,
  maxWidth = 'max-w-sm',
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [actualPosition, setActualPosition] = useState(position);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  // Enhanced positioning with viewport awareness and responsive sizing
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    // Additional responsive positions
    'top-start': 'bottom-full left-0 mb-2',
    'top-end': 'bottom-full right-0 mb-2',
    'bottom-start': 'top-full left-0 mt-2',
    'bottom-end': 'top-full right-0 mt-2',
  };

  const arrows = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900 dark:border-t-gray-100',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900 dark:border-b-gray-100',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900 dark:border-l-gray-100',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900 dark:border-r-gray-100',
    // Additional arrow positions
    'top-start': 'top-full left-4 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900 dark:border-t-gray-100',
    'top-end': 'top-full right-4 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900 dark:border-t-gray-100',
    'bottom-start': 'bottom-full left-4 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900 dark:border-b-gray-100',
    'bottom-end': 'bottom-full right-4 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900 dark:border-b-gray-100',
  };

  // Check viewport boundaries and adjust position
  const checkPosition = () => {
    if (!tooltipRef?.current || !triggerRef?.current) return;

    const tooltip = tooltipRef?.current;
    const trigger = triggerRef?.current;
    const rect = trigger?.getBoundingClientRect();
    const tooltipRect = tooltip?.getBoundingClientRect();
    const viewport = {
      width: window?.innerWidth,
      height: window?.innerHeight
    };

    let newPosition = position;

    // Check if tooltip would overflow viewport
    if (position === 'top' && rect?.top - tooltipRect?.height < 10) {
      newPosition = 'bottom';
    } else if (position === 'bottom' && rect?.bottom + tooltipRect?.height > viewport?.height - 10) {
      newPosition = 'top';
    } else if (position === 'left' && rect?.left - tooltipRect?.width < 10) {
      newPosition = 'right';
    } else if (position === 'right' && rect?.right + tooltipRect?.width > viewport?.width - 10) {
      newPosition = 'left';
    }

    // Check horizontal overflow for top/bottom positions
    if ((newPosition === 'top' || newPosition === 'bottom') && tooltipRect?.width > 0) {
      const centerX = rect?.left + rect?.width / 2;
      const tooltipHalfWidth = tooltipRect?.width / 2;
      
      if (centerX - tooltipHalfWidth < 10) {
        newPosition = newPosition + '-start';
      } else if (centerX + tooltipHalfWidth > viewport?.width - 10) {
        newPosition = newPosition + '-end';
      }
    }

    setActualPosition(newPosition);
  };

  const showTooltip = () => {
    if (disabled || !content) return;
    
    const id = setTimeout(() => {
      setIsVisible(true);
      // Check position after tooltip is rendered
      setTimeout(checkPosition, 0);
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        setTimeout(checkPosition, 0);
      }
    };

    window?.addEventListener('resize', handleResize);
    return () => window?.removeEventListener('resize', handleResize);
  }, [isVisible]);

  // Don't render tooltip wrapper if no content
  if (!content || disabled) {
    return children;
  }

  return (
    <div 
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      {...props}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            role="tooltip"
            className={cn(
              // Base styles with improved text wrapping and responsive width
              'absolute z-50 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-md pointer-events-none',
              // Responsive width handling - allow text to wrap properly
              'whitespace-normal break-words',
              // Dynamic max-width based on viewport
              'w-max min-w-[120px]',
              maxWidth,
              // Responsive max-width for different screen sizes
              'max-w-[280px] sm:max-w-sm md:max-w-md lg:max-w-lg',
              // Position classes
              positions?.[actualPosition],
              className
            )}
            style={{
              // Ensure tooltip doesn't exceed viewport width
              maxWidth: `min(${maxWidth?.replace('max-w-', '')}, calc(100vw - 20px))`
            }}
          >
            {content}
            <div className={cn('absolute w-0 h-0 border-4', arrows?.[actualPosition])} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tooltip;